/**
 * Identity Authentication Components
 * React components for authentication flow and protection
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useIdentityAuth } from './IdentityAuthProvider';
import { identityAuth } from './IdentityAuth';
import { getIdentityUrl as identityUrlForCurrentHost } from '../utils/config';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const typing = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const blink = keyframes`
  0%, 50% { border-color: transparent; }
  51%, 100% { border-color: #FFD700; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// Styled Components
const FullScreenContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #4A2C8C 0%, #6B4E9F 100%);
  position: relative;
  overflow: hidden;
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
  background: linear-gradient(135deg, #4A2C8C 0%, #6B4E9F 100%);
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
`;

const BackgroundPattern = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.1) 0%, transparent 50%);
  opacity: 0.3;
`;

const EduIntelligenceLogo = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: white;
  text-align: center;
  margin-bottom: 1rem;
  animation: ${fadeIn} 1s ease-out;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const EduIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #FFD700, #FFA500);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  animation: ${float} 3s ease-in-out infinite;
`;



const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  animation: ${fadeIn} 1s ease-out;
`;

const LoadingText = styled.div`
  font-size: 1.5rem;
  color: white;
  text-align: center;
  margin-bottom: 1rem;
`;

const TypingText = styled.div`
  font-size: 1.2rem;
  color: #FFD700;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  border-right: 2px solid #FFD700;
  animation: ${typing} 3s steps(40, end), ${blink} 0.75s step-end infinite;
  max-width: 400px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #FFD700;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const WelbeeLogo = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  width: 56px;
  height: 56px;
  background: #FFD700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: #4A2C8C;
  animation: ${fadeIn} 1s ease-out 1s both;
`;

const StatusMessage = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-top: 1rem;
  animation: ${fadeIn} 1s ease-out 1.5s both;
`;

// Auth Guard Component - Protects routes that require authentication
export const IdentityAuthGuard = ({ children }) => {
  const { authenticated, loading } = useIdentityAuth();
  const history = useHistory();

  React.useEffect(() => {
    if (!loading && !authenticated) {
      // Immediate redirect without showing loading screen
      const identityUrl = identityUrlForCurrentHost();
      if (identityUrl) {
        identityAuth.redirectToLogin();
      } else {
        console.error('IDENTITY_URL is not configured. Please check your .env file.');
      }
    }
  }, [authenticated, loading]);

  // If not authenticated, redirect immediately
  if (!loading && !authenticated) {
    return null; // Don't render anything while redirecting
  }

  if (loading) {
    return (
      <FullScreenContainer>
        <LeftPanel>
          <BackgroundPattern />
          <EduIntelligenceLogo>
            <EduIcon>📊</EduIcon>
            Edu Intelligence
          </EduIntelligenceLogo>
          <WelbeeLogo>W</WelbeeLogo>
        </LeftPanel>
        <RightPanel>
          <LoadingContainer>
            <LoadingText>🔐 Authenticating...</LoadingText>
            <TypingText>Loading Edu Intelligence...</TypingText>
            <LoadingSpinner />
            <StatusMessage>Please wait while we verify your session</StatusMessage>
          </LoadingContainer>
        </RightPanel>
      </FullScreenContainer>
    );
  }


  return children;
};

// Role-based Route Protection
export const RoleBasedGuard = ({ children, requiredRoles = [] }) => {
  const { user, loading } = useIdentityAuth();

  // Handle redirects at the top level to avoid conditional hooks
  React.useEffect(() => {
    if (!loading) {
      if (!user) {
        const identityUrl = identityUrlForCurrentHost();
      if (identityUrl) {
        window.location.href = `${identityUrl}/login`;
      } else {
        console.error('IDENTITY_URL is not configured. Please check your .env file.');
      }
        return;
      }

      const hasRequiredRole = requiredRoles.some(role => 
        user.roles.some(userRole => 
          userRole.toLowerCase().includes(role.toLowerCase())
        )
      );

      if (!hasRequiredRole) {
        const identityUrl = identityUrlForCurrentHost();
      if (identityUrl) {
        window.location.href = `${identityUrl}/login`;
      } else {
        console.error('IDENTITY_URL is not configured. Please check your .env file.');
      }
        return;
      }
    }
  }, [user, loading, requiredRoles]);

  if (loading) {
    return (
      <FullScreenContainer>
        <LeftPanel>
          <BackgroundPattern />
          <EduIntelligenceLogo>
            <EduIcon>📊</EduIcon>
            Edu Intelligence
          </EduIntelligenceLogo>
          <WelbeeLogo>W</WelbeeLogo>
        </LeftPanel>
        <RightPanel>
          <LoadingContainer>
            <LoadingText>🔐 Checking permissions...</LoadingText>
            <TypingText>Verifying your access rights...</TypingText>
            <LoadingSpinner />
            <StatusMessage>Please wait while we check your permissions</StatusMessage>
          </LoadingContainer>
        </RightPanel>
      </FullScreenContainer>
    );
  }

  // If not authenticated or insufficient permissions, don't render anything while redirecting
  if (!user || !requiredRoles.some(role => 
    user.roles.some(userRole => 
      userRole.toLowerCase().includes(role.toLowerCase())
    )
  )) {
    return null;
  }

  return children;
};

// Auth Callback Component - Handles redirects from Identity app
export const IdentityAuthCallback = () => {
  const { login } = useIdentityAuth();
  const history = useHistory();

  React.useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('IdentityAuthCallback: Processing authentication callback');
        
        // Check for URL parameters (if any)
        const urlParams = new URLSearchParams(window.location.search);
        const sessionParam = urlParams.get('session');
        
        // Attempt to authenticate
        const success = await login();
        
        if (success) {
          // Always redirect to super-admin-dashboard for admin app
          history.replace('/super-admin-dashboard');
        } else {
          history.replace('/');
        }
      } catch (error) {
        history.replace('/');
      }
    };

    handleCallback();
  }, [login, history]);

  return (
    <FullScreenContainer>
      <LeftPanel>
        <BackgroundPattern />
        <EduIntelligenceLogo>
          <EduIcon>📊</EduIcon>
          Edu Intelligence
        </EduIntelligenceLogo>
        <WelbeeLogo>W</WelbeeLogo>
      </LeftPanel>
      <RightPanel>
        <LoadingContainer>
          <LoadingText>🔄 Processing authentication...</LoadingText>
          <TypingText>Completing your login process...</TypingText>
          <LoadingSpinner />
          <StatusMessage>Please wait while we complete your login</StatusMessage>
        </LoadingContainer>
      </RightPanel>
    </FullScreenContainer>
  );
};

// Super Admin Landing Component - Always redirects to super-admin-dashboard
export const SuperAdminLanding = () => {
  const { user, loading } = useIdentityAuth();
  const history = useHistory();

  React.useEffect(() => {
    if (!loading) {
      if (user) {
        // Always redirect to super-admin-dashboard for admin app
        history.replace('/super-admin-dashboard');
      } else {
        // No user found, redirect to login
        const identityUrl = identityUrlForCurrentHost();
      if (identityUrl) {
        identityAuth.redirectToLogin();
      } else {
        console.error('IDENTITY_URL is not configured. Please check your .env file.');
      }
      }
    }
  }, [user, loading, history]);

  // If no user and not loading, redirect immediately
  if (!loading && !user) {
    return null; // Don't render anything while redirecting
  }

  if (loading) {
    return (
      <FullScreenContainer>
        <LeftPanel>
          <BackgroundPattern />
          <EduIntelligenceLogo>
            <EduIcon>📊</EduIcon>
            Edu Intelligence
          </EduIntelligenceLogo>
          <WelbeeLogo>W</WelbeeLogo>
        </LeftPanel>
        <RightPanel>
          <LoadingContainer>
            <LoadingText>🔐 Loading...</LoadingText>
            <TypingText>Setting up your admin dashboard...</TypingText>
            <LoadingSpinner />
            <StatusMessage>Please wait while we set up your admin dashboard</StatusMessage>
          </LoadingContainer>
        </RightPanel>
      </FullScreenContainer>
    );
  }

  return (
    <FullScreenContainer>
      <LeftPanel>
        <BackgroundPattern />
        <EduIntelligenceLogo>
          <EduIcon>📊</EduIcon>
          Edu Intelligence
        </EduIntelligenceLogo>
        <WelbeeLogo>W</WelbeeLogo>
      </LeftPanel>
      <RightPanel>
        <LoadingContainer>
          <LoadingText>🚀 Redirecting...</LoadingText>
          <TypingText>Taking you to your admin dashboard...</TypingText>
          <LoadingSpinner />
          <StatusMessage>Welcome to Edu Intelligence Admin!</StatusMessage>
        </LoadingContainer>
      </RightPanel>
    </FullScreenContainer>
  );
};
